import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { plusDays } from '../../lib/countryFacts';
import {
  animalOf,
  localDay,
  lunarOf,
  monthAround,
  monthName,
  type LocalCalendar,
} from '../../lib/localCalendar';

interface Props {
  /** The calendar the month is laid out by. */
  own: LocalCalendar;
  /** Its weekend, Monday 1 to Sunday 7. */
  weekend: number[];
  /** The day its week starts on, Monday 1 to Sunday 7. */
  weekStart: number;
  /** A day of the month shown. */
  shown: string;
  /** Another month asked for, by a day of it. */
  onShow: (day: string) => void;
  /** A day as the calendar draws it: by its number here, ours under it, its weekend shaded. */
  cell: (day: string, number: number, ours: string | undefined, off: boolean) => ReactNode;
  /** The trip's first and last day: no month before or after them asked for. */
  trip: { first: string; last: string };
  /** The ways and money of the days from one to before another, as ours says them. */
  summary: (first: string, next: string) => string;
  /** The ways' colours and whose travel is shown, beside the month's name as in ours. */
  keys: ReactNode;
  /** What the days show, under the month. */
  legend: ReactNode;
}

/**
 * A month as a country's own calendar lays it out (the Calendars mockup): from its first day to
 * its last, each day by its own number and ours under it, where it is spent; its weekend shaded.
 */
export function LocalMonth({
  own,
  weekend,
  weekStart,
  shown,
  onShow,
  trip,
  summary,
  keys,
  cell,
  legend,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'et' ? 'et-EE' : 'en-GB';
  const { first, next } = monthAround(shown, own.system);
  const all: string[] = [];
  for (let day = first; day < next; day = plusDays(day, 1)) all.push(day);
  // Monday 1 to Sunday 7, the week from the day it starts on there.
  const isoDay = (day: string) => ((new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7) + 1;
  const lead = (isoDay(first) - weekStart + 7) % 7;
  const ours = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const weekdays = Array.from({ length: 7 }, (_, at) => {
    const day = ((weekStart - 1 + at) % 7) + 1;
    return {
      // 5 January 2026 a Monday.
      name: new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
        Date.UTC(2026, 0, 4 + day),
      ),
      off: weekend.includes(day),
    };
  });
  const moon = lunarOf(first, own.system);
  const title = own.lunar
    ? `${t('local.lunarMonth', { month: moon.month })} · ${t('local.animalYear', {
        animal: t(`local.animal.${String(animalOf(first, own.system))}`),
      })}`
    : monthName(first, own.system, locale);
  return (
    <section className="panel calendar-card local-month" aria-label={title}>
      <div className="calendar-card-head">
        <div className="month-head">
          <button
            type="button"
            className="icon-button"
            aria-label={t('calendar.previous')}
            disabled={first <= trip.first}
            onClick={() => {
              onShow(plusDays(first, -1));
            }}
          >
            ‹
          </button>
          {/* Under its name, as written there. */}
          <div className="local-month-title">
            <h2>{title}</h2>
            <span className="quiet" lang={own.locale}>
              {monthName(first, own.system, own.locale)}
            </span>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label={t('calendar.next')}
            disabled={next > trip.last}
            onClick={() => {
              onShow(next);
            }}
          >
            ›
          </button>
          <span className="quiet month-summary">{summary(first, next)}</span>
        </div>
        {keys}
      </div>
      <div className="month-grid local-grid">
        {weekdays.map(({ name, off }) => (
          <span
            key={name}
            className={off ? 'weekday weekday-off' : 'weekday'}
            aria-hidden="true"
          >
            {name}
          </span>
        ))}
        {Array.from({ length: lead }, (_, at) => (
          <span key={`lead-${String(at)}`} />
        ))}
        {all.map((day) => {
          const weekday = isoDay(day);
          return cell(
            day,
            localDay(day, own.system),
            // Where its months are ours, our date would say its number again.
            own.ourMonths ? undefined : ours.format(Date.parse(`${day}T00:00:00Z`)),
            weekend.includes(weekday),
          );
        })}
      </div>
      {legend}
    </section>
  );
}
